<template>
  <div class="overflow-x-hidden">
    <!-- =========================================================
         HERO
         ========================================================= -->
    <section class="relative bg-hero-fade pt-14 pb-24 sm:pt-20 sm:pb-32">
      <!-- Background grid -->
      <div class="absolute inset-0 -z-10 bg-grid-ink bg-grid-32 [mask-image:linear-gradient(to_bottom,white,transparent_85%)] opacity-60"></div>

      <!-- Animated gradient blobs -->
      <div class="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <div class="absolute -top-40 -left-20 w-[32rem] h-[32rem] rounded-full bg-brand-200/30 blur-3xl animate-blob-slow"></div>
        <div class="absolute top-20 -right-32 w-[28rem] h-[28rem] rounded-full bg-accent-300/20 blur-3xl animate-blob-slower"></div>
        <div class="absolute bottom-0 left-1/3 w-[20rem] h-[20rem] rounded-full bg-ink-100/40 blur-3xl animate-blob-slow" style="animation-delay: 2s"></div>
      </div>

      <div class="container-wide">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <!-- Copy -->
          <div class="lg:col-span-6 min-w-0">
            <div class="eyebrow reveal">
              <span class="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
              The SEO intelligence platform
            </div>

            <h1 class="font-display text-5xl sm:text-6xl lg:text-[4.25rem] leading-[1.02] text-ink-900 mt-6 text-balance reveal reveal-1">
              Site audits
              <em class="italic text-brand-700">that win clients</em>
            </h1>

            <p class="mt-5 text-lg text-ink-600 max-w-xl text-pretty leading-relaxed reveal reveal-2">
              LeadRanks is an SEO and <router-link to="/geo-optimization" class="text-brand-700 underline decoration-brand-200 hover:decoration-brand-500 underline-offset-2">AI-search (GEO)</router-link> audit platform: run a 100+ point technical, content, and AI-readiness scan, then find real businesses who need the fixes you can deliver. Stop chasing customers — let SEO bring them to you.
            </p>

            <!-- Inline analyzer -->
            <form @submit.prevent="startAnalysis" class="mt-8 reveal reveal-3">
              <div class="flex flex-col sm:flex-row gap-2 max-w-xl">
                <div class="input-group flex-1">
                  <span class="input-group-addon">
                    <Icon name="globe" :size="16" />
                  </span>
                  <input
                    v-model="url"
                    type="text"
                    placeholder="yourdomain.com"
                    class="input"
                    aria-label="Website URL"
                  />
                </div>
                <button type="submit" class="btn-brand btn-lg btn-glow whitespace-nowrap">
                  Run free audit
                  <Icon name="arrow-right" :size="16" />
                </button>
              </div>
              <div class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs text-ink-500">
                <span class="flex items-center gap-1.5">
                  <Icon name="check-circle" :size="14" classes="text-accent-600" />
                  No credit card required
                </span>
                <span class="flex items-center gap-1.5">
                  <Icon name="check-circle" :size="14" classes="text-accent-600" />
                  First report in 3 min
                </span>
                <span class="flex items-center gap-1.5">
                  <Icon name="check-circle" :size="14" classes="text-accent-600" />
                  Cancel anytime
                </span>
              </div>
            </form>
          </div>

          <!-- Hero visual: a stylized live-audit card -->
          <div class="lg:col-span-6 min-w-0 reveal reveal-4">
            <HeroAuditPreview />
          </div>
        </div>
      </div>
    </section>

    <!-- =========================================================
         LEADRANKS INTEL TICKER — animated feature promo
         ========================================================= -->
    <section class="relative bg-ink-900 overflow-hidden border-b border-ink-800">
      <div class="absolute inset-0 bg-grid-ink bg-grid-16 opacity-10"></div>
      <div class="container-wide py-4">
        <div class="flex items-center gap-3 overflow-hidden" style="mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)">
          <div class="flex items-center gap-3 whitespace-nowrap animate-marquee">
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse"></span>
              100+ point SEO audit in 3–5 minutes
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" style="animation-delay: 0.3s"></span>
              AI-powered GEO readiness for ChatGPT, Perplexity &amp; Gemini
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" style="animation-delay: 0.2s"></span>
              Connect GSC &amp; GA4 for live traffic data
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-warn-500 animate-pulse" style="animation-delay: 0.6s"></span>
              Core Web Vitals + Lighthouse in one report
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" style="animation-delay: 0.2s"></span>
              Auto-discover competitors &amp; benchmark in real-time
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" style="animation-delay: 0.5s"></span>
              E-E-A-T quality scoring with AI recommendations
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" style="animation-delay: 0.4s"></span>
              PDF exports, white-label branding, agency-ready
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" style="animation-delay: 0.1s"></span>
              Warm lead finder — contact-ready prospects, not just a list
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <!-- repeated for seamless loop -->
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse"></span>
              100+ point SEO audit in 3–5 minutes
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" style="animation-delay: 0.3s"></span>
              AI-powered GEO readiness for ChatGPT, Perplexity &amp; Gemini
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" style="animation-delay: 0.2s"></span>
              Connect GSC &amp; GA4 for live traffic data
            </span>
            <span class="text-ink-600 text-xs">✦</span>
            <span class="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-warn-500 animate-pulse" style="animation-delay: 0.6s"></span>
              Core Web Vitals + Lighthouse in one report
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- =========================================================
         WORKFLOW — the real 3-step user journey (Audit -> Find ->
         Pitch). Distinct from "HOW IT WORKS" further down, which is
         about the Quick/Standard/Advanced analysis-DEPTH tiers, not
         this flow -- kept a different name/heading deliberately so the
         two don't read as duplicates. Order matches the hero's own
         copy verbatim ("run a scan... then find real businesses who
         need the fixes") rather than inventing a different sequence.
         ========================================================= -->
    <section class="py-24 bg-white border-b border-ink-100">
      <div class="container-wide">
        <div class="max-w-2xl mb-14">
          <div class="eyebrow mb-4"><Icon name="target" :size="12" /> The workflow</div>
          <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
            From URL to client, in <em class="italic text-brand-700">three steps</em>.
          </h2>
          <p class="mt-4 text-ink-600 text-lg">
            No juggling five separate tools. Audit a site, find who else needs the
            same fix, and reach out — start to finish, in one platform.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div v-for="(step, i) in workflowSteps" :key="step.title" class="reveal" :class="`reveal-${i + 1}`">
            <div class="flex items-center gap-3 mb-4">
              <span class="w-9 h-9 rounded-full bg-brand-600 text-white font-display text-lg flex items-center justify-center shrink-0">{{ i + 1 }}</span>
              <Icon :name="step.icon" :size="20" classes="text-brand-600" />
            </div>
            <h3 class="font-semibold text-ink-900 text-lg">{{ step.title }}</h3>
            <p class="mt-2 text-sm text-ink-600 leading-relaxed">{{ step.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- =========================================================
         BENEFITS GRID — 6 core benefits, AI mentioned as one
         ========================================================= -->
    <section class="py-28 bg-white">
      <div class="container-wide">
        <div class="max-w-2xl mb-14">
          <div class="eyebrow mb-4"><Icon name="sparkles" :size="12" /> Everything you need</div>
          <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
            Everything your <em class="italic text-brand-700">SEO stack</em> is missing.
          </h2>
          <p class="mt-4 text-ink-600 text-lg">
            Stop stitching together five different tools. Everything a freelancer, agency, or in-house team needs to audit, track rankings, discover new clients, and optimize for AI search — in one platform.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <BenefitCard
            v-for="(b, i) in benefits"
            :key="b.title"
            :icon="b.icon"
            :title="b.title"
            :body="b.body"
            :accent="b.accent"
            :class="`reveal reveal-${Math.min(i + 1, 6)}`"
          />
        </div>
      </div>
    </section>

    <!-- =========================================================
         HOW IT WORKS — the 3-tier analysis experience
         ========================================================= -->
    <section class="py-28 bg-ink-50/40 border-y border-ink-100 relative overflow-hidden">
      <div class="absolute inset-0 -z-10 bg-dot-ink bg-dot-20 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"></div>

      <div class="container-wide">
        <div class="max-w-2xl mb-14">
          <div class="eyebrow mb-4"><Icon name="layers" :size="12" /> Three ways to analyze</div>
          <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
            Start free. Scale to <em class="italic text-brand-700">white-label reporting</em>.
          </h2>
          <p class="mt-4 text-ink-600 text-lg">
            Pick the depth you need. Every plan runs the same audit engine — Lighthouse, PageSpeed, backlink graph, Core Web Vitals, and 17 analysis modules.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TierCard
            mode="Quick Scan"
            badge="Free"
            description="The same full audit as Standard and Advanced — nothing held back. Use this when you just want the headline score."
            :features="['All 17 analysis modules', 'Core Web Vitals, backlinks & keywords', 'Metadata + structured data', '5 free audits total, no credit card']"
            icon="zap"
            accent="ink"
          />
          <TierCard
            mode="Standard"
            badge="Most popular"
            description="Same engine, same depth — this is the default most people pick for ongoing tracking."
            :features="['Everything in Quick Scan', 'PDF, CSV & JSON export', 'Historical tracking', 'More audits & competitors on paid plans']"
            icon="bar-chart"
            accent="brand"
            highlighted
          />
          <TierCard
            mode="Advanced"
            badge="Most thorough"
            description="Same full audit, plus forced full-page JavaScript rendering — for React/Vue/Angular and other JS-heavy sites. Uses more compute, so it's on Pro and above."
            :features="['Everything in Standard', 'Full JS rendering (headless browser)', 'SEO Assistant on every plan (unlimited on paid)', 'White-label reporting on Agency']"
            icon="sparkles"
            accent="accent"
          />
        </div>
      </div>
    </section>

    <!-- =========================================================
         WHAT WE ANALYZE — 17 modules grid
         ========================================================= -->
    <section class="py-28 bg-white">
      <div class="container-wide">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div class="lg:col-span-5 lg:sticky lg:top-24">
            <div class="eyebrow mb-4"><Icon name="target" :size="12" /> Depth of analysis</div>
            <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
              17 analysis modules.
              <em class="italic text-brand-700">One report.</em>
            </h2>
            <p class="mt-4 text-ink-600 text-lg">
              Every audit runs the full stack: technical SEO, content quality, metadata, performance, links, schema, and more — synthesized into a single prioritized action plan.
            </p>
            <div class="mt-6 flex flex-wrap gap-2">
              <span class="chip chip-active">Technical SEO</span>
              <span class="chip">Content</span>
              <span class="chip">Performance</span>
              <span class="chip">Backlinks</span>
              <span class="chip">Keywords</span>
              <span class="chip">Schema</span>
            </div>
          </div>

          <div class="lg:col-span-7 grid grid-cols-2 gap-3">
            <ModuleTile v-for="m in modules" :key="m.name" v-bind="m" />
          </div>
        </div>
      </div>
    </section>

    <!-- =========================================================
         STATS STRIP
         ========================================================= -->
    <section class="py-24 bg-ink-900 text-white relative overflow-hidden">
      <div class="absolute inset-0 -z-0 opacity-30"
           style="background: radial-gradient(600px 300px at 20% 50%, rgba(51,103,255,.35), transparent 60%), radial-gradient(500px 250px at 80% 50%, rgba(16,185,129,.25), transparent 60%)"></div>
      <div class="container-wide relative">
        <!-- Every figure here is a real, verifiable product fact -- no
             placeholder/estimated numbers. If a stat can't be stated
             factually (e.g. real analytics-derived counts), it's left out
             rather than approximated. -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-10">
          <StatBlock label="Audit modules" value="40+" />
          <StatBlock label="AI crawler bots checked" value="22" />
          <StatBlock label="Data points per report" value="100+" />
          <StatBlock label="Concurrent users supported" value="100+" />
        </div>

        <!-- Research strip — our own measured data, not vendor estimates.
             Same dark section as the capacity stats above on purpose:
             this row is the one a competitor cannot copy. Figures come
             from the study at /learn/ai-crawler-access-australian-agencies. -->
        <div class="mt-14 pt-10 border-t border-white/10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div class="lg:col-span-5">
            <div class="text-xs font-semibold uppercase tracking-[0.14em] text-white/50 mb-3">Original research</div>
            <h3 class="font-display text-2xl sm:text-3xl text-white leading-tight text-balance">
              We don't estimate. <em class="italic text-brand-300">We measure.</em>
            </h3>
            <p class="mt-3 text-white/70 text-pretty">
              We read the robots.txt of 191 Australian agency sites ourselves — no vendor data, no estimates.
            </p>
            <router-link
              to="/learn/ai-crawler-access-australian-agencies"
              class="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors"
            >
              Read the full study
              <Icon name="arrow-right" :size="14" />
            </router-link>
          </div>
          <div class="lg:col-span-7 grid grid-cols-3 gap-6 lg:gap-8">
            <StatBlock label="Agency sites scanned" value="191" />
            <StatBlock label="Block an AI crawler" value="5%" />
            <StatBlock label="Publish an llms.txt" value="49%" />
          </div>
          <p class="lg:col-span-12 text-2xs text-ink-400">
            Agencies only. Counted from live robots.txt, September 2026. Method and full data published in the write-up.
          </p>
        </div>
      </div>
    </section>

    <!-- =========================================================
         KEY FACTS (semantic list, not a visual grid — AI answer engines
         extract facts from plain <ul>/<li> markup more reliably than from
         decorative card layouts. Every line below is a real, verifiable
         product fact — nothing estimated or fabricated. -->
    <section class="py-20 bg-white border-t border-ink-100">
      <div class="container-wide max-w-3xl">
        <div class="eyebrow mb-4"><Icon name="list" :size="12" /> Key facts</div>
        <ul class="space-y-3 text-ink-700 leading-relaxed">
          <li class="flex items-start gap-3">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>LeadRanks runs 40+ SEO and GEO audit modules covering technical health, content quality, and AI-readiness in a single scan.</span>
          </li>
          <li class="flex items-start gap-3">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>AI crawler accessibility is checked against 22 bot user-agents, including GPTBot, Claude-Bot, PerplexityBot, and Google-Extended.</span>
          </li>
          <li class="flex items-start gap-3">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>Unlike most SEO tools, LeadRanks also surfaces real prospects — businesses whose sites match the issues you can fix, complete with contact details.</span>
          </li>
          <li class="flex items-start gap-3">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>Agencies can white-label the entire platform, including a custom domain, for client-facing delivery.</span>
          </li>
          <li class="flex items-start gap-3">
            <Icon name="check-circle" :size="16" classes="text-accent-600 mt-0.5 shrink-0" />
            <span>Every report includes 100+ data points, from Core Web Vitals to E-E-A-T signals to AI citation readiness.</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- =========================================================
         COMPARISON — LeadRanks vs. doing it manually / hiring a VA.
         Numbers on the LeadRanks side are real (the same "under 3
         minutes" and free-tier price used verbatim elsewhere on this
         page); the manual/VA columns are general, widely-known market
         context (typical VA hourly rates), not a specific claim about
         any one competitor or person -- kept qualitative where a real
         number isn't knowable (e.g. "hours" per manual audit, not a
         fabricated precise figure).
         ========================================================= -->
    <section class="py-24 bg-white border-b border-ink-100">
      <div class="container-wide">
        <div class="max-w-2xl mb-14">
          <div class="eyebrow mb-4"><Icon name="zap" :size="12" /> The alternative</div>
          <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
            Versus doing it <em class="italic text-brand-700">yourself, or by hand</em>.
          </h2>
          <p class="mt-4 text-ink-600 text-lg">
            Manual audits and a VA both work — they just don't scale the
            same way.
          </p>
        </div>

        <!-- Desktop/tablet: real table, horizontally scrollable within its
             own container so it never causes page-level overflow. Hidden
             below sm: a 4-column table with real sentence-length content
             is unreadable squeezed into a 375px scroll strip, not just a
             layout risk -- same "stack on mobile instead of shrinking a
             wide table" call already made for PricingPage.vue's Full
             Comparison table (CLAUDE.md #33). -->
        <!-- Styled to match PricingPage.vue's own "featured tier" language
             (border-2 border-brand-500 + a solid brand ribbon badge, see
             CLAUDE.md #16/#66) rather than inventing a new visual
             treatment -- same signal ("this is the one to look at"),
             consistent with how the rest of the site marks a winner. -->
        <div class="hidden sm:block overflow-x-auto scroll-custom rounded-2xl border-2 border-brand-500 shadow-card">
          <table class="w-full text-sm min-w-[600px]">
            <thead class="bg-ink-50/60 border-b border-ink-100">
              <tr>
                <th class="text-left px-5 py-4 font-semibold text-ink-700">&nbsp;</th>
                <th class="text-left px-4 py-4 font-semibold text-ink-700">Doing it yourself</th>
                <th class="text-left px-4 py-4 font-semibold text-ink-700">Hiring a VA</th>
                <th class="text-left px-4 py-4 font-bold text-white bg-brand-600">
                  <span class="inline-flex items-center gap-1.5"><Icon name="pass" :size="14" /> LeadRanks</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparisonRows" :key="row.label" class="border-b border-ink-100 last:border-0">
                <td class="px-5 py-3.5 text-ink-700 font-medium">{{ row.label }}</td>
                <td class="px-4 py-3.5 text-ink-600">{{ row.diy }}</td>
                <td class="px-4 py-3.5 text-ink-600">{{ row.va }}</td>
                <td class="px-4 py-3.5 text-ink-900 font-semibold bg-brand-50">{{ row.leadranks }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile: one card per metric, each answer labeled instead of
             relying on column position -- a scrollable 600px table read
             fine on a tablet but was cramped and hard to compare on a
             phone. Only 5 rows, so a plain stack (not an accordion) reads
             fastest with zero extra taps. -->
        <div class="sm:hidden space-y-3">
          <div v-for="row in comparisonRows" :key="row.label" class="rounded-2xl border-2 border-brand-500 shadow-card p-4">
            <div class="text-sm font-semibold text-ink-900 mb-3">{{ row.label }}</div>
            <dl class="space-y-2.5 text-sm">
              <div class="flex items-start justify-between gap-3">
                <!-- text-ink-500, not -400 (PageSpeed a11y finding,
                     2026-09-09): ink-400 (#8a8983) on white is 3.51:1,
                     below WCAG AA's 4.5:1 floor for normal text. ink-500
                     (#65645f) is the next step up the ramp and clears it
                     at 5.93:1 -- the smallest change that actually passes. -->
                <dt class="text-ink-500 shrink-0">Doing it yourself</dt>
                <dd class="text-ink-600 text-right">{{ row.diy }}</dd>
              </div>
              <div class="flex items-start justify-between gap-3">
                <dt class="text-ink-500 shrink-0">Hiring a VA</dt>
                <dd class="text-ink-600 text-right">{{ row.va }}</dd>
              </div>
              <div class="flex items-start justify-between gap-3 rounded-lg bg-brand-600 px-2.5 py-2 -mx-2.5">
                <dt class="text-white font-bold shrink-0 flex items-center gap-1"><Icon name="pass" :size="13" /> LeadRanks</dt>
                <dd class="text-white font-semibold text-right">{{ row.leadranks }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- One clear CTA after the comparison, not five identical links.
             Each "LeadRanks" cell/bar above stays a plain data point --
             turning all 5 into their own link to the same destination
             would be repetitive (and a poor screen-reader experience: five
             consecutive links reading "3-5 minutes", "Same 100+ point
             checklist"... all going to the same place). Links to the
             general pricing page, not straight to Pro specifically -- the
             table's own bottom row already says "Free to start, from
             $49/mo", so sending every click straight past the free tier
             would contradict that framing. -->
        <div class="mt-8 text-center">
          <router-link to="/pricing" class="btn-primary btn-lg">
            See full pricing <Icon name="arrow-right" :size="16" />
          </router-link>
        </div>
      </div>
    </section>

    <!-- =========================================================
         FAQ
         ========================================================= -->
    <section class="py-24 lg:py-32 bg-ink-50/40 border-y border-ink-100">
      <div class="container-wide max-w-3xl">
        <div class="text-center mb-12">
          <div class="eyebrow mx-auto w-fit"><Icon name="info" :size="12" /> FAQ</div>
          <h2 class="font-display text-4xl mt-3 text-ink-900">Frequently asked</h2>
        </div>

        <div class="divide-y divide-ink-100 card !rounded-2xl">
          <details v-for="(faq, i) in faqs" :key="i" class="group p-5 lg:p-6">
            <summary class="flex items-start justify-between gap-4 cursor-pointer list-none">
              <span class="text-base font-medium text-ink-900">{{ faq.question }}</span>
              <Icon name="chevron-down" :size="16" class="text-ink-400 group-open:rotate-180 transition-transform shrink-0 mt-0.5" />
            </summary>
            <p class="mt-3 text-sm text-ink-600 leading-relaxed pr-10">
              {{ faq.answer }}
            </p>
          </details>
        </div>
      </div>
    </section>

    <!-- =========================================================
         FINAL CTA
         ========================================================= -->
    <section class="py-28 relative">
      <div class="container-tight">
        <div class="card p-12 md:p-16 text-center relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-white border border-brand-100">
          <div class="absolute inset-0 -z-10 bg-grid-ink bg-grid-32 opacity-30 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"></div>

          <h2 class="font-display text-4xl sm:text-5xl text-ink-900 leading-tight text-balance">
            Your next ranking win
            <br class="hidden sm:block" />
            starts with one audit.
          </h2>
          <p class="mt-4 text-ink-600 max-w-xl mx-auto">
            Free, no credit card. Run your first analysis and see exactly what Google — and AI search — sees on your site right now.
          </p>
          <div class="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <router-link to="/analyze" class="btn-brand btn-lg btn-glow">
              Run free audit <Icon name="arrow-right" :size="16" />
            </router-link>
            <router-link to="/pricing" class="btn-outline btn-lg">
              See pricing
            </router-link>
          </div>
          <p class="mt-5 text-xs text-ink-400 flex items-center justify-center gap-1.5">
            <Icon name="clock" :size="12" />
            Results in minutes — every day you wait is a day a competitor closes the gap.
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, inject } from 'vue';
import { useRouter } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';

// usePageMeta's reactive (getter-function) form is used here specifically
// so jsonLd can be built directly from `faqs` below without worrying about
// script-level declaration order -- apply() only runs in onMounted, by
// which point every const in this file has already initialized. Never a
// second, hand-maintained copy of the FAQ content that could drift from
// what's actually visible (same rule PricingPage.vue's own FAQ follows).
usePageMeta(() => ({
  title: 'LeadRanks — SEO & GEO Audits + Lead Finder for Agencies',
  meta: [
    { name: 'description', content: 'Run a 100+ point SEO and AI-search (GEO) audit, then find the businesses that need the fixes. Free audit, no credit card required — see your score in minutes.' },
    { property: 'og:title', content: 'LeadRanks — SEO & GEO Audits + Lead Finder for Agencies' },
    { property: 'og:description', content: 'Run a 100+ point SEO and AI-search (GEO) audit, then find the businesses that need the fixes. Free audit, no credit card required — see your score in minutes.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ],
}));
import HeroAuditPreview from '../home/HeroAuditPreview.vue';
import BenefitCard from '../home/BenefitCard.vue';
import TierCard from '../home/TierCard.vue';
import ModuleTile from '../home/ModuleTile.vue';
import StatBlock from '../home/StatBlock.vue';

const router = useRouter();
const userStore = inject('userStore', null);

const url = ref('');

const startAnalysis = () => {
  const v = url.value.trim();
  if (!v) return;
  // If logged in, go to /analyze with url prefilled. Otherwise, register flow.
  // Allow all users — /api/public/analyze handles guest users
  router.push({ path: '/analyze', query: { url: v } });
};

const workflowSteps = [
  { icon: 'search', title: 'Audit', body: 'Enter any URL and run a 100+ point technical, content, and AI-readiness (GEO) scan — first report back in 3–5 minutes.' },
  { icon: 'users', title: 'Find', body: 'Search for real businesses with the same fixable problems your audit just surfaced — contact-ready prospects, not a cold list.' },
  { icon: 'mail', title: 'Pitch', body: 'Export the report and lead contact info, start from an AI-drafted outreach email, and reach out — you edit and send.' },
];

const comparisonRows = [
  { label: 'Time per audit', diy: 'Hours, per site', va: 'Hours, per site — plus training and review time', leadranks: '3–5 minutes' },
  { label: 'Consistency', diy: 'Depends what you remember to check', va: 'Depends on their training', leadranks: 'Same 100+ point checklist, every time' },
  { label: 'AI-search (GEO) readiness', diy: 'Not typically checked', va: 'Not typically checked', leadranks: 'Built in' },
  { label: 'Finding new prospects', diy: 'A separate manual search', va: 'A separate manual search', leadranks: 'Built in (Find Leads)' },
  { label: 'Ongoing cost', diy: 'Your own time', va: 'Typically $3–15/hr, ongoing', leadranks: 'Free to start, from $49/mo' },
];

const benefits = [
  { icon: 'zap',        title: '3-minute audit',           accent: 'brand',
    body: 'Full 100+ point technical SEO analysis finishes while your coffee is still warm. Live WebSocket progress means you see exactly what\'s happening.' },
  { icon: 'sparkles',   title: 'AI copilot',               accent: 'accent',
    body: 'Chat assistant built into your dashboard. Ask "what should I fix first?" and get a prioritized plan with estimated ranking impact.' },
  { icon: 'bar-chart',  title: 'Competitor tracking',      accent: 'brand',
    body: 'Benchmark against up to 3 competitors. See keyword gaps, backlink overlap, and where you\'re winning or losing.' },
  { icon: 'cpu',        title: 'GEO / AI search readiness',accent: 'accent',
    body: 'See how ChatGPT, Perplexity, and Google AI Overviews would cite your page — schema, quotability, and citation authority scored out of 100, not a pass/fail checkbox.' },
  { icon: 'users',      title: 'Warm lead finder',         accent: 'brand',
    body: 'Turn weak spots on real businesses\' sites into a prospect list. Every lead is scored against the same audit engine your own report runs on — contact-ready, not just a name.' },
  { icon: 'shield',     title: 'White-label & API',        accent: 'brand',
    body: 'Brand reports with your logo and colors. REST API for bulk analysis, webhooks on completion. Built for agencies.' },
];

const modules = [
  { icon: 'search',       name: 'Technical audit',    tag: 'robots, sitemap, canonicals' },
  { icon: 'target',       name: 'Metadata analysis',  tag: 'titles, descriptions, OG tags' },
  { icon: 'book',         name: 'Content quality',    tag: 'readability, density, structure' },
  { icon: 'trending-up',  name: 'Core Web Vitals',    tag: 'LCP · FCP · CLS · TBT' },
  { icon: 'zap',          name: 'Lighthouse',         tag: 'performance, a11y, best practices' },
  { icon: 'bar-chart',    name: 'PageSpeed',          tag: 'mobile & desktop' },
  { icon: 'link',         name: 'Link checker',       tag: 'broken links, 404s, redirects' },
  { icon: 'shield',       name: 'Backlink profile',   tag: 'DA, referring domains, spam' },
  { icon: 'globe',        name: 'Keyword research',   tag: 'volume, competition, intent' },
  { icon: 'layers',       name: 'Schema markup',      tag: 'structured data, rich snippets' },
  { icon: 'bookmark',     name: 'Social tags',        tag: 'OpenGraph, Twitter cards' },
  { icon: 'info',         name: 'Accessibility',      tag: 'WCAG contrast, alt text, ARIA' },
];

const faqs = [
  { question: 'What does a LeadRanks audit actually check?',
    answer: 'Every audit runs 17 analysis modules covering technical SEO, content quality, Core Web Vitals, backlink profile, structured data, and AI Engine Optimization (GEO) — how ChatGPT, Perplexity, and Google\'s AI Overviews see your site. It\'s all compiled into one prioritized action plan, not a wall of raw data.' },
  { question: 'How does competitor benchmarking work?',
    answer: 'Add up to 3 competitor domains, or let LeadRanks auto-discover them. Every report includes a side-by-side comparison — where you\'re ahead, where you\'re behind, and the specific keywords they rank for that you don\'t.' },
  { question: 'What is GEO, and why does it matter?',
    answer: 'GEO (Generative Engine Optimization) measures whether AI answer engines can actually find, understand, and cite your content. It\'s a fast-growing part of search that most traditional SEO tools don\'t check at all.' },
  { question: 'What is the Find Leads feature?',
    answer: 'A prospecting tool that searches for real businesses with real, fixable SEO problems — sites that need exactly what you offer. Built for agencies and freelancers who want warm outreach targets, not a cold list.' },
  { question: 'Is there a free plan?',
    answer: 'Yes — the Starter plan includes 5 full audits and 3 lead credits to try Find Leads, no credit card required. Upgrade only when you need more audits, monthly lead credits, tracked domains, or white-label reporting.' },
  { question: 'Can I put my own branding on reports?',
    answer: 'Yes, on the Agency plan — swap in your logo, colors, and a custom domain so your clients see your agency delivering the audit, not LeadRanks.' },
  { question: 'How is LeadRanks different from Ahrefs or SEMrush?',
    answer: 'Ahrefs and SEMrush are keyword and backlink research tools — you look things up. LeadRanks is built around a different workflow: audit a site, then find other real businesses with the same problems, then reach out to them — all in one platform. It also scores GEO (AI-search readiness for ChatGPT, Perplexity, and AI Overviews), which neither of those tools checks at all.' },
  { question: 'What kind of websites can I audit?',
    answer: 'Any public website — WordPress, Shopify, a custom-built site, a single-page app, anything reachable over HTTP(S). There\'s no platform restriction; the audit fetches and analyzes the real, live page.' },
  { question: 'Is the outreach email copy actually usable, or generic AI filler?',
    answer: 'It\'s a real starting draft, grounded in that lead\'s actual audit findings — not a generic template. You\'re meant to review and personalize it before sending, not blind-send it; think first draft, not final copy.' },
];
</script>
